import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  MessageCircle, 
  Briefcase, 
  User, 
  DollarSign,
  Calendar,
  Trash2,
  CheckCheck
} from 'lucide-react';

const NotificationsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    page: 1,
    unread: false
  });

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery(
    ['notifications', filters],
    () => api.get(API_ENDPOINTS.NOTIFICATIONS.LIST, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  // Mark as read mutation
  const markReadMutation = useMutation(
    (id) => api.put(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotifications']);
      }
    }
  );

  // Mark all as read mutation
  const markAllReadMutation = useMutation(
    () => api.put(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ),
    {
      onSuccess: () => {
        toast.success('All notifications marked as read');
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotifications']);
      }
    }
  );

  // Delete notification mutation
  const deleteMutation = useMutation(
    (id) => api.delete(API_ENDPOINTS.NOTIFICATIONS.DELETE(id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['unreadNotifications']);
      }
    }
  );

  const notifications = notificationsData?.notifications || [];
  const totalPages = notificationsData?.totalPages || 1;
  const currentPage = notificationsData?.currentPage || 1;

  const handleMarkAsRead = (notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification._id);
    }
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      job_application: Briefcase,
      job_approved: CheckCircle,
      job_rejected: XCircle,
      gig_order: Briefcase,
      gig_approved: CheckCircle,
      gig_rejected: XCircle,
      message: MessageCircle,
      payment: DollarSign,
      system: Bell
    };
    const Icon = icons[type] || Bell;
    return <Icon className="w-5 h-5" />;
  };

  const getNotificationColor = (type) => {
    const colors = {
      job_application: 'text-blue-500',
      job_approved: 'text-green-500',
      job_rejected: 'text-red-500',
      gig_order: 'text-purple-500',
      gig_approved: 'text-green-500',
      gig_rejected: 'text-red-500',
      message: 'text-blue-500',
      payment: 'text-green-500',
      system: 'text-gray-500'
    };
    return colors[type] || 'text-gray-500';
  };

  const getNotificationLink = (notification) => {
    if (notification.data?.chatId) {
      return `/chats/${notification.data.chatId}`;
    }
    if (notification.data?.jobId) {
      return `/jobs/${notification.data.jobId}`;
    }
    if (notification.data?.gigId) {
      return `/gigs/${notification.data.gigId}`;
    }
    return '#';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading mb-2">
              Notifications
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Stay updated with your latest activities
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setFilters(prev => ({ ...prev, unread: !prev.unread }))}
              className={`btn-outline ${filters.unread ? 'bg-accent text-white' : ''}`}
            >
              {filters.unread ? 'Show All' : 'Unread Only'}
            </button>
            {notifications.some(n => !n.read) && (
              <button
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isLoading}
                className="btn-primary"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                {markAllReadMutation.isLoading ? 'Marking...' : 'Mark All Read'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.map((notification) => (
          <div 
            key={notification._id} 
            className={`card p-6 hover:shadow-lg transition-all duration-200 ${
              !notification.read ? 'border-l-4 border-accent bg-accent/5' : ''
            }`}
          >
            <div className="flex items-start space-x-4">
              {/* Icon */}
              <div className={`flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                {getNotificationIcon(notification.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {notification.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(notification.createdAt).toLocaleDateString()}</span>
                      </div>
                      {!notification.read && (
                        <span className="px-2 py-1 bg-accent text-white text-xs rounded-full">
                          New
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.read && (
                      <button
                        onClick={() => handleMarkAsRead(notification)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors duration-200"
                        title="Mark as read"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(notification._id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                      title="Delete notification"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Action Button */}
                {getNotificationLink(notification) !== '#' && (
                  <div className="mt-4">
                    <Link
                      to={getNotificationLink(notification)}
                      onClick={() => handleMarkAsRead(notification)}
                      className="btn-outline text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            onClick={() => setFilters(prev => ({ ...prev, page: currentPage - 1 }))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = Math.max(1, currentPage - 2) + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => setFilters(prev => ({ ...prev, page }))}
                className={`px-3 py-2 text-sm rounded-lg ${
                  page === currentPage
                    ? 'bg-accent text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => setFilters(prev => ({ ...prev, page: currentPage + 1 }))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* No Notifications */}
      {notifications.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔔</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {filters.unread ? 'No unread notifications' : 'No notifications yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {filters.unread 
              ? 'You\'re all caught up! No unread notifications.' 
              : 'You\'ll receive notifications for job applications, approvals, and other activities here.'
            }
          </p>
          {filters.unread && (
            <button
              onClick={() => setFilters(prev => ({ ...prev, unread: false }))}
              className="btn-primary"
            >
              View All Notifications
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
